import { useEffect, useState, useCallback, useRef, Suspense, Component } from "react";
import Spinner from "./components/Spinner";
import { ThemeProvider } from "next-themes";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { auth } from "./firebase";
import {
  onAuthStateChanged,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { apiFetch, invalidateTokenCache } from "./lib/api";
import { API } from "./config/env";
import Layout from "./components/Layout";
import { useIdleDetection } from "./hooks/useIdleDetection";
import IdleTimerModal from "./components/IdleTimerModal";
import GitHubPATBanner from "./components/GitHubPATBanner";
import PWAInstallBanner from "./components/PWAInstallBanner";
import { DemoBanner } from "./components/DemoBanner";
import { lazyWithRetry, isLazyLoadError, shouldAutoReloadLazyError, clearLazyReloadAttempt } from "./lib/lazyWithRetry";
import { AuthContext, useAuth } from "./authContext";
import { submitFeedback } from "./components/ReportProblemDialog";
import { startWebVitalsObserver, trackClientError } from "./lib/telemetry";

export { useAuth } from "./authContext";

// Route-level code splitting — each page is loaded on demand
const LoginPage = lazyWithRetry(() => import("./pages/LoginPage"));
const RegisterPage = lazyWithRetry(() => import("./pages/RegisterPage"));
const AuthActionPage = lazyWithRetry(() => import("./pages/AuthActionPage"));
// Sub-dashboard lazy imports at App.js module scope — parallel with Dashboard chunk,
// eliminating the double lazy-load waterfall (M30 T245).
const EmployeeDashboard = lazyWithRetry(() => import("./pages/dashboards/EmployeeDashboard"));
const ManagerDashboard = lazyWithRetry(() => import("./pages/dashboards/ManagerDashboard"));
const AdminDashboard = lazyWithRetry(() => import("./pages/dashboards/AdminDashboard"));
const ExecutiveDashboard = lazyWithRetry(() => import("./pages/dashboards/ExecutiveDashboard"));
const WorkOrders = lazyWithRetry(() => import("./pages/WorkOrders"));
const WorkOrderDetail = lazyWithRetry(() => import("./pages/WorkOrderDetail"));
const WorkOrderForm = lazyWithRetry(() => import("./pages/WorkOrderForm"));
const TimeEntry = lazyWithRetry(() => import("./pages/TimeEntry"));
const Timesheet = lazyWithRetry(() => import("./pages/Timesheet"));
const Reports = lazyWithRetry(() => import("./pages/Reports"));
const UserManagement = lazyWithRetry(() => import("./pages/UserManagement"));
const OperatorDashboard = lazyWithRetry(() => import("./pages/OperatorDashboard"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const Clients = lazyWithRetry(() => import("./pages/Clients"));
const ClientDetail = lazyWithRetry(() => import("./pages/ClientDetail"));
const ClientForm = lazyWithRetry(() => import("./pages/ClientForm"));
const Projects = lazyWithRetry(() => import("./pages/Projects"));
const ProjectDetail = lazyWithRetry(() => import("./pages/ProjectDetail"));
const ProjectForm = lazyWithRetry(() => import("./pages/ProjectForm"));
const Approvals = lazyWithRetry(() => import("./pages/Approvals"));
const Invoices = lazyWithRetry(() => import("./pages/Invoices"));
const TeamDashboard = lazyWithRetry(() => import("./pages/TeamDashboard"));
const ClientPortal = lazyWithRetry(() => import("./pages/ClientPortal"));
const ServiceCatalogSettings = lazyWithRetry(() => import("./pages/ServiceCatalogSettings"));
const StudioSettings = lazyWithRetry(() => import("./pages/StudioSettings")); // M66 ADR-030
const WOTemplateManager = lazyWithRetry(() => import("./pages/WOTemplateManager")); // M22 T170
const WOTemplateGroupManager = lazyWithRetry(() => import("./pages/WOTemplateGroupManager")); // M29 T229
const AuditLog = lazyWithRetry(() => import("./pages/AuditLog")); // M7 T53
const GitHubActivityPage = lazyWithRetry(() => import("./pages/GitHubActivityPage")); // M39 T289
const RecurringEntries = lazyWithRetry(() => import("./pages/RecurringEntries")); // M45 T319

export { API };

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTimer, setActiveTimer] = useState(null);
  const [timerDraft, setTimerDraft] = useState({ workOrderId: "", description: "" });

  useEffect(() => {
    let cancelled = false;
    // Hard ceiling so a hung Firebase/network call cannot leave the app on a blank spinner.
    const safetyTimer = setTimeout(() => {
      if (!cancelled) {
        console.warn("[Auth] safety timeout — clearing loading state");
        setLoading(false);
      }
    }, 15000);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      if (firebaseUser) {
        const controller = new AbortController();
        const abortTimer = setTimeout(() => controller.abort(), 12000);
        try {
          const idToken = await firebaseUser.getIdToken(true);
          // Prefer relative /api on same origin (Hosting → Cloud Run rewrite). Absolute
          // BACKEND_URL still works; AbortController avoids AbortSignal.timeout (missing on older Safari).
          const response = await fetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${idToken}` },
            signal: controller.signal,
          });
          if (cancelled) return;
          if (response.ok) {
            let userData = await response.json();
            const orgFromToken = (() => {
              try {
                const payload = idToken.split(".")[1];
                const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
                return json.org_id || null;
              } catch {
                return null;
              }
            })();
            if (!userData.org_id && orgFromToken) {
              userData.org_id = orgFromToken;
            }
            try {
              const mine = await fetch(`${API}/auth/my-tenants`, {
                headers: { Authorization: `Bearer ${idToken}` },
                signal: controller.signal,
              });
              if (mine.ok) {
                const membership = await mine.json();
                const orgs = membership.orgs || [];
                if (orgs.length === 1 && orgs[0].org_id && orgs[0].org_id !== userData.org_id) {
                  const selected = await fetch(`${API}/auth/select-org`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
                    body: JSON.stringify({ org_id: orgs[0].org_id }),
                    signal: controller.signal,
                  });
                  if (!selected.ok) {
                    console.warn("select-org failed", selected.status);
                  } else {
                    invalidateTokenCache();
                    const refreshed = await firebaseUser.getIdToken(true);
                    const again = await fetch(`${API}/auth/me`, {
                      headers: { Authorization: `Bearer ${refreshed}` },
                      signal: controller.signal,
                    });
                    if (again.ok) userData = await again.json();
                  }
                }
              }
            } catch (err) {
              console.warn("membership lookup failed; continuing with /auth/me", err);
            }
            setUser(userData);
          } else {
            let detail = "";
            try {
              const body = await response.json();
              detail = typeof body?.detail === "string" ? body.detail : "";
            } catch {
              /* ignore non-JSON error bodies */
            }
            const msg = response.status >= 500
              ? "Cannot connect to server. Please try again later."
              : (detail || "Signed in, but the app could not load your account. Try again.");
            const pendingInvite = typeof sessionStorage !== "undefined" && sessionStorage.getItem("pendingInviteToken");
            const skipSignOut = response.status === 403 && /not registered/i.test(detail) && pendingInvite;
            if (!skipSignOut) {
              toast.error(msg);
            }
            console.error("[Auth] /auth/me responded", response.status, detail);
            setUser(null);
            if ((response.status === 401 || response.status === 403) && !skipSignOut) {
              await firebaseSignOut(auth).catch(() => {});
            }
          }
        } catch (err) {
          if (cancelled) return;
          console.error("[Auth] session setup failed", err);
          const aborted = err?.name === "AbortError";
          const msg = String(err?.message || err || "");
          // Distinguish Firebase token refresh failures (often Safari/authDomain) from API outages
          if (/auth\//i.test(err?.code || "") || /token|network-request-failed|indexedDB/i.test(msg)) {
            toast.error("Sign-in could not finish on this browser. Try email sign-in, or clear site data and retry.");
          } else if (aborted) {
            toast.error("Server took too long to respond. Please try again.");
          } else {
            toast.error("Cannot connect to server. Please ensure the backend is running.");
          }
          setUser(null);
          await firebaseSignOut(auth).catch(() => {});
        } finally {
          clearTimeout(abortTimer);
        }
      } else {
        setUser(null);
        setActiveTimer(null);
        setTimerDraft({ workOrderId: "", description: "" });
      }
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
      clearTimeout(safetyTimer);
      unsubscribe();
    };
  }, []);

  const login = (userData) => setUser(userData);

  const logout = async () => {
    await firebaseSignOut(auth);
    setUser(null);
  };

  const getToken = async (forceRefresh = false) => {
    const currentUser = auth.currentUser;
    return currentUser ? currentUser.getIdToken(forceRefresh) : null;
  };

  const orgId = user?.org_id ?? null;

  return (
    <AuthContext.Provider value={{ user, orgId, setUser, loading, login, logout, getToken, activeTimer, setActiveTimer, timerDraft, setTimerDraft }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-subtle flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    toast.error("Access denied");
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function PATStatusMonitor() {
  const { user } = useAuth();
  const [patDays, setPatDays] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const hasChecked = useRef(false);

  useEffect(() => {
    if (!user) {
      setPatDays(null);
      setDismissed(false);
      hasChecked.current = false;
      return;
    }
    const load = () => {
      apiFetch("/users/me/github-pat/status")
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data?.has_pat && data.days_until_expiry !== null && data.days_until_expiry !== undefined) {
            setPatDays(data.days_until_expiry);
          } else {
            setPatDays(null);
          }
        })
        .catch(() => {});
    };
    if (!hasChecked.current) {
      hasChecked.current = true;
      load();
    }
    const onUpdated = () => load();
    window.addEventListener("github-pat-updated", onUpdated);
    return () => window.removeEventListener("github-pat-updated", onUpdated);
  }, [user]);

  if (dismissed || patDays === null) return null;

  return (
    <GitHubPATBanner
      daysUntilExpiry={patDays}
      onDismiss={() => setDismissed(true)}
    />
  );
}

function TimerMonitor() {
  const { activeTimer, setActiveTimer, timerDraft, setTimerDraft } = useAuth();
  const [idleModal, setIdleModal] = useState({ open: false, idleMinutes: 0 });

  const handleIdle = useCallback((idleMinutes) => {
    setIdleModal({ open: true, idleMinutes });
  }, []);

  const { resetIdle } = useIdleDetection({
    enabled: !!activeTimer,
    thresholdMs: 30 * 60 * 1000,
    onIdle: handleIdle,
  });

  const stopTimerApi = useCallback(async () => {
    try {
      await apiFetch("/timer/stop", { method: "POST" });
    } finally {
      // Always clear UI regardless of API response — server may have already stopped the timer
      setActiveTimer(null);
      setTimerDraft({ workOrderId: "", description: "" });
    }
  }, [setActiveTimer, setTimerDraft]);

  // Close idle UI after the originating click settles so the same pointer-up
  // cannot fall through onto TimerBar's Stop button (opens TimerStopDialog
  // with elapsed 0:00:00 and a timer that is already gone).
  const closeIdleModalSoon = useCallback(() => {
    window.setTimeout(() => {
      setIdleModal({ open: false, idleMinutes: 0 });
      resetIdle();
    }, 150);
  }, [resetIdle]);

  const handleDeductStop = async () => {
    const mins = idleModal.idleMinutes;
    // Stop first while the idle dialog still covers the page, then dismiss.
    await stopTimerApi();
    toast.success(`Timer stopped. ${mins}m idle time noted.`);
    closeIdleModalSoon();
  };

  const handleKeepRunning = () => {
    setIdleModal({ open: false, idleMinutes: 0 });
    resetIdle();
  };

  const handleStopKeepAll = async () => {
    await stopTimerApi();
    toast.success("Timer stopped. Full time logged.");
    closeIdleModalSoon();
  };

  return (
    <IdleTimerModal
      open={idleModal.open}
      idleMinutes={idleModal.idleMinutes}
      onDeductStop={handleDeductStop}
      onKeepRunning={handleKeepRunning}
      onStopKeepAll={handleStopKeepAll}
    />
  );
}

class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "", stack: "", reporting: false, reported: false };
  }
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: String(error?.message || error || "Unknown error"),
      stack: String(error?.stack || ""),
    };
  }
  componentDidCatch(error, info) {
    console.error("[ChunkErrorBoundary]", error, info?.componentStack);
    this._componentStack = info?.componentStack || "";
    if (isLazyLoadError(error) && shouldAutoReloadLazyError()) {
      // Hard navigation avoids HTTP-cached SPA shells (max-age on /users etc.).
      const url = new URL(window.location.href);
      url.searchParams.set("_cb", String(Date.now()));
      window.location.replace(url.toString());
    }
  }
  reportCrash = async () => {
    if (this.state.reporting || this.state.reported) return;
    this.setState({ reporting: true });
    try {
      await submitFeedback({
        kind: "crash",
        message: this.state.message || "Chunk / render failure",
        stack: [this.state.stack, this._componentStack].filter(Boolean).join("\n"),
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
      });
      toast.success("Crash report sent");
      this.setState({ reported: true });
    } catch (err) {
      toast.error(err?.message || "Could not send report");
    } finally {
      this.setState({ reporting: false });
    }
  };
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-brand-surface flex flex-col items-center justify-center gap-4 px-4">
          <p className="text-brand-steel text-sm">Failed to load this page.</p>
          {this.state.message ? (
            <p className="text-xs text-brand-steel max-w-md text-center font-mono break-all">
              {this.state.message}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={this.reportCrash}
              disabled={this.state.reporting || this.state.reported}
              className="px-4 py-2 text-sm border border-brand-border rounded-md hover:bg-brand-subtle disabled:opacity-50"
              data-testid="crash-report-button"
            >
              {this.state.reported ? "Reported" : this.state.reporting ? "Reporting…" : "Report"}
            </button>
            <button
              type="button"
              onClick={() => {
                clearLazyReloadAttempt();
                try {
                  // Force main.jsx bustStaleClientCaches to run again on next load.
                  Object.keys(localStorage)
                    .filter((k) => k.startsWith("tymlogr-sw-bust-"))
                    .forEach((k) => localStorage.removeItem(k));
                } catch { /* ignore */ }
                const url = new URL(window.location.origin + "/");
                url.searchParams.set("_cb", String(Date.now()));
                window.location.replace(url.toString());
              }}
              className="px-4 py-2 text-sm bg-brand-ink text-brand-on-ink rounded-md hover:bg-brand-ink/90"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Role-aware dashboard router — resolves in App.js so all sub-dashboard chunks
// are registered at module scope and can begin downloading in parallel with Dashboard.jsx.
function DashboardRouter() {
  const { user, loading } = useAuth();
  if (loading || !user) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }
  const role = user.role ?? "employee";
  if (role === "admin") return <AdminDashboard />;
  if (role === "manager") return <ManagerDashboard />;
  if (role === "executive") return <ExecutiveDashboard />;
  return <EmployeeDashboard />;
}

function AppRouter() {
  return (
    <ChunkErrorBoundary>
    <Suspense fallback={
      <div className="min-h-screen bg-brand-subtle flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    }>
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/action" element={<AuthActionPage />} />
      {/* Public portal route — token-based, no auth required */}
      <Route path="/portal" element={<ClientPortal />} />

      {/* Protected routes — Layout renders ONCE as persistent parent */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<DashboardRouter />} />
        <Route path="/work-orders" element={<WorkOrders />} />
        <Route path="/work-orders/new" element={
          <ProtectedRoute allowedRoles={["admin","manager"]}><WorkOrderForm /></ProtectedRoute>
        } />
        <Route path="/work-orders/:id" element={<WorkOrderDetail />} />
        <Route path="/work-orders/:id/edit" element={
          <ProtectedRoute allowedRoles={["admin","manager"]}><WorkOrderForm /></ProtectedRoute>
        } />
        <Route path="/time-entry" element={<TimeEntry />} />
        <Route path="/timesheet" element={<Timesheet />} />
        <Route path="/recurring-entries" element={<RecurringEntries />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/users" element={
          <ProtectedRoute allowedRoles={["admin"]}><UserManagement /></ProtectedRoute>
        } />
        <Route path="/operator" element={
          <ProtectedRoute allowedRoles={["admin"]}><OperatorDashboard /></ProtectedRoute>
        } />
        <Route path="/audit-log" element={
          <ProtectedRoute allowedRoles={["admin"]}><AuditLog /></ProtectedRoute>
        } />
        <Route path="/github-activity" element={
          <ProtectedRoute allowedRoles={["admin","manager","employee","executive"]}><GitHubActivityPage /></ProtectedRoute>
        } />
        <Route path="/settings/service-catalog" element={
          <ProtectedRoute allowedRoles={["admin"]}><ServiceCatalogSettings /></ProtectedRoute>
        } />
        <Route path="/settings/studio" element={
          <ProtectedRoute allowedRoles={["admin"]}><StudioSettings /></ProtectedRoute>
        } />
        <Route path="/settings/wo-templates" element={
          <ProtectedRoute allowedRoles={["admin"]}><WOTemplateManager /></ProtectedRoute>
        } />
        <Route path="/settings/wo-template-groups" element={
          <ProtectedRoute allowedRoles={["admin"]}><WOTemplateGroupManager /></ProtectedRoute>
        } />
        <Route path="/clients" element={
          <ProtectedRoute allowedRoles={["admin","manager"]}><Clients /></ProtectedRoute>
        } />
        <Route path="/clients/new" element={
          <ProtectedRoute allowedRoles={["admin","manager"]}><ClientForm /></ProtectedRoute>
        } />
        <Route path="/clients/:id" element={
          <ProtectedRoute allowedRoles={["admin","manager"]}><ClientDetail /></ProtectedRoute>
        } />
        <Route path="/clients/:id/edit" element={
          <ProtectedRoute allowedRoles={["admin","manager"]}><ClientForm /></ProtectedRoute>
        } />
        <Route path="/projects" element={
          <ProtectedRoute allowedRoles={["admin","manager"]}><Projects /></ProtectedRoute>
        } />
        <Route path="/projects/new" element={
          <ProtectedRoute allowedRoles={["admin","manager"]}><ProjectForm /></ProtectedRoute>
        } />
        <Route path="/projects/:id" element={
          <ProtectedRoute allowedRoles={["admin","manager"]}><ProjectDetail /></ProtectedRoute>
        } />
        <Route path="/projects/:id/edit" element={
          <ProtectedRoute allowedRoles={["admin","manager"]}><ProjectForm /></ProtectedRoute>
        } />
        <Route path="/approvals" element={
          <ProtectedRoute allowedRoles={["admin","manager"]}><Approvals /></ProtectedRoute>
        } />
        <Route path="/invoices" element={
          <ProtectedRoute allowedRoles={["admin","manager"]}><Invoices /></ProtectedRoute>
        } />
        <Route path="/team" element={
          <ProtectedRoute allowedRoles={["admin","manager"]}><TeamDashboard /></ProtectedRoute>
        } />
        <Route path="/profile" element={<Profile />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
    </ChunkErrorBoundary>
  );
}

function DemoBannerMount() {
  const { user } = useAuth();
  if (!user?.is_demo) return null;
  return <DemoBanner />;
}

function DogfoodTelemetryBoot() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user) return undefined;
    startWebVitalsObserver();
    const onReject = (ev) => {
      const msg = String(ev?.reason?.message || ev?.reason || "unhandledrejection");
      if (/Loading chunk|Failed to fetch dynamically/i.test(msg)) return;
      trackClientError("unhandledrejection", { message: msg.slice(0, 200) });
    };
    window.addEventListener("unhandledrejection", onReject);
    return () => window.removeEventListener("unhandledrejection", onReject);
  }, [user?.user_id || user?.id]);
  return null;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <DemoBannerMount />
          <DogfoodTelemetryBoot />
          <PATStatusMonitor />
          <TimerMonitor />
          <PWAInstallBanner />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
